import { TemplateRef } from '@angular/core';
import {
  CardIconMode,
  DatasetTableColumnConfig,
  HeaderTemplateContext,
  CellTemplateContext,
} from '../ui-dataset-table.model';
import {
  createHeaderWithTooltipConfig,
  DynamicHeaderConfig,
  HeaderWithTooltipOptions,
} from './header-template.helper';

/**
 * Builder class for creating typed DatasetTableColumnConfig objects with a fluent API
 *
 * @example
 * const builder = new ColumnBuilder<MyType>();
 * const column = builder.column('name')
 *   .label('TRANSLATIONS.NAME')
 *   .width('minmax(200px, 1fr)')
 *   .sortable()
 *   .mobileVisible()
 *   .build();
 */
export class ColumnBuilder<T> {
  /**
   * Creates a basic column configuration builder
   * @param key - The property key from the entity type T
   */
  public column(key: keyof T): ColumnConfigBuilder<T> {
    return new ColumnConfigBuilder<T>(key);
  }

  /**
   * Creates a text column configuration builder with common text defaults
   * @param key - The property key from the entity type T
   */
  public textColumn(key: keyof T): ColumnConfigBuilder<T> {
    return new ColumnConfigBuilder<T>(key).truncate();
  }

  /**
   * Creates a date column configuration builder
   * @param key - The property key from the entity type T
   */
  public dateColumn(key: keyof T): ColumnConfigBuilder<T> {
    return new ColumnConfigBuilder<T>(key).width('minmax(150px, 1fr)');
  }

  /**
   * Creates a number column configuration builder
   * @param key - The property key from the entity type T
   */
  public numberColumn(key: keyof T): ColumnConfigBuilder<T> {
    return new ColumnConfigBuilder<T>(key).width('minmax(80px, 0.5fr)');
  }
}

/**
 * Fluent builder for individual column configuration
 */
type InternalColumnConfig<T> = DatasetTableColumnConfig<T> & {
  _headerTooltipConfig?: DynamicHeaderConfig;
};

export class ColumnConfigBuilder<T> {
  private config: InternalColumnConfig<T>;

  constructor(key: keyof T) {
    this.config = {
      key,
      label: null,
    };
  }

  /**
   * Sets the column label (translation key)
   * @param translationKey - The i18n translation key for the column header
   */
  public label(translationKey: string): this {
    this.config.label = translationKey;
    return this;
  }

  /**
   * Sets the column width (CSS grid value)
   * @param width - CSS grid width value (e.g., 'minmax(200px, 1fr)', '150px', '1fr')
   */
  public width(width: string): this {
    this.config.width = width;
    return this;
  }

  /**
   * Marks the column as sortable
   */
  public sortable(enabled = true): this {
    if (enabled) {
      this.config.sortable = true;
    }
    return this;
  }

  /**
   * Sets the column visibility
   * @param isVisible - Whether the column is visible (default: true)
   */
  public visible(isVisible = true): this {
    this.config.visible = isVisible;
    return this;
  }

  /**
   * Marks the column as visible on mobile devices
   */
  public mobileVisible(): this {
    this.config.mobileVisible = true;
    return this;
  }

  /**
   * Enables text truncation for the column
   */
  public truncate(): this {
    this.config.truncate = true;
    return this;
  }

  /**
   * Centers the cell content alignment on desktop
   */
  public alignCenter(): this {
    this.config.alignCenter = true;
    return this;
  }

  /**
   * Sets an icon to display in card layout
   * @param iconClass - CSS icon class or function (e.g., 'ui-icon-camera', (row) => 'ui-icon-status-online')
   */
  public cardIcon(iconClass: string | ((row: T) => string | null)): this {
    this.config.cardIcon = iconClass;
    return this;
  }

  /**
   * Sets display mode for card layout
   * @param mode - 'icon-only' | 'icon-with-label' | 'label-only'
   */
  public cardMode(mode: CardIconMode): this {
    this.config.cardMode = mode;
    return this;
  }

  /**
   * Makes field value bold in card layout
   */
  public cardBoldValue(): this {
    this.config.cardBoldValue = true;
    return this;
  }

  public cardOrder(order: number): this {
    this.config.cardOrder = order;
    return this;
  }

  /**
   * Sets label for details modal. If not provided, uses column.label.
   * Useful when column.label is empty for table header but needs a label in details.
   * @param labelKey - Translation key for the details modal label
   */
  public detailsModalLabel(labelKey: string | null): this {
    this.config.detailsModalLabel = labelKey;
    return this;
  }

  /**
   * Sets a custom header template for the column
   * @param template - Angular TemplateRef for the header
   */
  public headerTemplate(template: TemplateRef<HeaderTemplateContext<T>>): this {
    this.config.headerTemplate = template;
    return this;
  }

  /**
   * Sets a custom cell template for the column
   * @param template - Angular TemplateRef for the cell
   */
  public cellTemplate(template: TemplateRef<CellTemplateContext<T>>): this {
    this.config.cellTemplate = template;
    return this;
  }

  /**
   * Sets a value formatter function for the column
   * @param formatter - Function to format the cell value
   */
  public formatter(
    formatter: (value: T[keyof T], row: T) => string | number,
  ): this {
    this.config.valueFormatter = formatter;
    return this;
  }

  /**
   * Creates a header with label + info icon + tooltip
   * Eliminates need for custom header templates
   *
   * @param labelKey - Translation key for the label
   * @param tooltipKey - Translation key for the tooltip
   * @param options - Optional tooltip position and class
   *
   * @example
   * builder.column('_id')
   *   .headerWithTooltip('MOBILE', 'MOBILE_TOOLTIP')
   *   .cellTemplate(template)
   *   .mobileVisible(false)
   *   .build()
   */
  public headerWithTooltip(
    labelKey: string,
    tooltipKey: string,
    options?: HeaderWithTooltipOptions,
  ): this {
    this.config.label = labelKey;
    this.config._headerTooltipConfig = createHeaderWithTooltipConfig(
      labelKey,
      tooltipKey,
      options,
    );
    return this;
  }

  /**
   * Extends the configuration with custom properties
   * Useful for adding properties not covered by the fluent API
   * @param partialConfig - Partial configuration object to merge
   */
  public extend(partialConfig: Partial<DatasetTableColumnConfig<T>>): this {
    Object.assign(this.config, partialConfig);
    return this;
  }

  /**
   * Builds and returns the final column configuration
   */
  public build(): DatasetTableColumnConfig<T> {
    return this.config;
  }
}
